// Rich message composition for the Teams sidebar: a document model, and the one
// place it is turned into HTML.
//
// ─── WHY THIS SHAPE ──────────────────────────────────────────────────────────
//
// Sending `contentType: 'html'` means ATLAS puts markup into other people's
// Teams clients, including people outside Chauvet. The danger in that is never
// "HTML" as such — it is **assembling markup by concatenating user text**, where
// one unescaped `<` turns a message into an injection.
//
// So the client never sends HTML. It sends this document model, the route
// validates it with Zod, and `teamsNodesToHtml` below is the ONLY place markup
// is constructed. Every text leaf is escaped on the way out and every tag is
// generated from a closed set — the user cannot contribute a tag, an attribute,
// or an unescaped angle bracket, whatever they type and whatever a hostile
// client posts. Injection is not mitigated here; it is unrepresentable.
//
// This is the mirror image of `teams-html.ts`, which turns Graph's HTML into
// plain text on the way in. Both directions keep author markup out of any
// renderer we do not control.
//
// Verified against live Graph 2026-07-22: Teams preserves b/strong, i, u, s,
// ul/ol/li, blockquote and anchors exactly as sent, and rewrites `<pre><code>`
// to its own `<codeblock>` with the content intact.
/** Narrowing helper, so call sites do not each invent their own `in` check. */
export function isConversationMention(node) {
    return 'conversationId' in node;
}
/**
 * Structural bounds, enforced by the route's schema as well as here.
 *
 * A document is composed in a sidebar, not authored in a CMS. These exist so a
 * malformed or hostile payload cannot turn one request into unbounded work —
 * they are a denial-of-service bound, and are deliberately far above anything a
 * person types into a 320px panel.
 */
export const TEAMS_COMPOSE_MAX_BLOCKS = 200;
export const TEAMS_COMPOSE_MAX_INLINE_PER_BLOCK = 200;
export const TEAMS_COMPOSE_MAX_LIST_ITEMS = 100;
/**
 * Only these schemes may reach an `href`.
 *
 * `javascript:` is the obvious one; `data:` is the one people forget, and it is
 * just as good for delivering a payload.
 */
const ALLOWED_LINK_SCHEMES = new Set(['http:', 'https:']);
export function isAllowedLinkHref(href) {
    try {
        return ALLOWED_LINK_SCHEMES.has(new URL(href).protocol);
    }
    catch {
        // Relative or unparseable. A message going to somebody else's Teams client
        // has no useful notion of "relative to ATLAS", so this is never valid.
        return false;
    }
}
/**
 * Escape a text leaf. Applied to EVERY piece of user-supplied text without
 * exception — including link text and code — before it is placed in markup.
 *
 * `"` and `'` are escaped as well as the angle brackets: attribute values are
 * built from user data (the `href`), and an unescaped quote there would break
 * out of the attribute and let a new one be introduced.
 */
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/** Wrap escaped content in whichever mark tags are set. Order is stable. */
function applyMarks(escaped, marks) {
    let html = escaped;
    if (marks.code)
        html = `<code>${html}</code>`;
    if (marks.bold)
        html = `<b>${html}</b>`;
    if (marks.italic)
        html = `<i>${html}</i>`;
    if (marks.underline)
        html = `<u>${html}</u>`;
    if (marks.strike)
        html = `<s>${html}</s>`;
    return html;
}
function inlineToHtml(node, mentionId) {
    if (node.type === 'mention') {
        // No marks: Teams renders mentions in its own style, and wrapping one in
        // <b> makes it a bold mention rather than a bolder person.
        const id = mentionId?.(node);
        const text = escapeHtml(node.text);
        return id === undefined ? text : `<at id="${id}">${text}</at>`;
    }
    if (node.type === 'link') {
        const text = escapeHtml(node.text || node.href);
        // A link we will not vouch for degrades to its own text — the reader still
        // sees what was written, and nothing is silently dropped, but no anchor is
        // created. Never emit the href in that case, not even escaped.
        if (!isAllowedLinkHref(node.href))
            return applyMarks(text, node);
        return applyMarks(`<a href="${escapeHtml(node.href)}">${text}</a>`, node);
    }
    return applyMarks(escapeHtml(node.text), node);
}
function inlinesToHtml(children, mentionId) {
    return children.map((child) => inlineToHtml(child, mentionId)).join('');
}
/**
 * The document model → Teams-flavoured HTML. The only markup-producing function
 * in the codebase's send path.
 *
 * Empty paragraphs become `<p>&nbsp;</p>` rather than being dropped: a blank
 * line between two paragraphs is deliberate authoring, and Teams collapses an
 * empty `<p>`.
 */
export function teamsNodesToHtml(blocks, mentionId) {
    return blocks
        .map((block) => {
        switch (block.type) {
            case 'paragraph': {
                const inner = inlinesToHtml(block.children, mentionId);
                return `<p>${inner || '&nbsp;'}</p>`;
            }
            case 'quote':
                return `<blockquote>${inlinesToHtml(block.children, mentionId)}</blockquote>`;
            case 'codeBlock':
                // No marks and no links inside a code block — its content is exactly
                // what was typed, escaped once.
                return `<pre><code>${escapeHtml(block.text)}</code></pre>`;
            case 'list': {
                const tag = block.ordered ? 'ol' : 'ul';
                const items = block.items
                    .map((item) => `<li>${inlinesToHtml(item.children, mentionId)}</li>`)
                    .join('');
                return `<${tag}>${items}</${tag}>`;
            }
        }
    })
        .join('');
}
/**
 * The document as plain text.
 *
 * Used for the length check, the optimistic echo, and as the fallback body when
 * a message has no formatting worth sending as HTML. Deriving it from the same
 * model that produces the markup is what keeps the two from disagreeing.
 */
export function teamsNodesToText(blocks) {
    return blocks
        .map((block) => {
        switch (block.type) {
            case 'paragraph':
            case 'quote':
                return block.children.map((child) => child.text).join('');
            case 'codeBlock':
                return block.text;
            case 'list':
                return block.items
                    .map((item, index) => {
                    const bullet = block.ordered ? `${index + 1}. ` : '• ';
                    return bullet + item.children.map((child) => child.text).join('');
                })
                    .join('\n');
        }
    })
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/**
 * Whether a document carries anything HTML would preserve that plain text would
 * not.
 *
 * A message with no formatting is sent as `contentType: 'text'`, exactly as
 * before this feature existed — which keeps the safest path the default and the
 * common one, and means a plain message is byte-identical to what ATLAS has been
 * sending since phase 3.
 */
export function teamsNodesNeedHtml(blocks) {
    return blocks.some((block) => {
        if (block.type !== 'paragraph')
            return true;
        return block.children.some((child) => child.type === 'link' ||
            child.type === 'mention' ||
            !!(child.bold || child.italic || child.underline || child.strike || child.code));
    });
}
/**
 * The body and the mentions array, produced together.
 *
 * This is the only function that should build a message destined for Graph when
 * mentions are involved: the `<at id="N">` in the HTML and the `id` in the
 * mentions array have to agree, and generating them in one pass is what
 * guarantees it. The same person mentioned twice gets one entry and one id,
 * which is what Teams itself sends.
 */
export function teamsNodesToGraphMessage(blocks) {
    const mentions = [];
    // Namespaced, so a conversation id can never collide with an object id and
    // hand a group mention the entry belonging to a person.
    const idsByTarget = new Map();
    const mentionId = (node) => {
        const isConversation = isConversationMention(node);
        const key = isConversation ? `conversation:${node.conversationId}` : `user:${node.userId}`;
        const existing = idsByTarget.get(key);
        if (existing !== undefined)
            return existing;
        const id = mentions.length;
        idsByTarget.set(key, id);
        mentions.push({
            id,
            // Graph echoes `mentionText` back verbatim; it is data, not markup, and is
            // escaped only where it lands inside the HTML body.
            mentionText: node.text,
            mentioned: isConversation
                ? {
                    conversation: {
                        id: node.conversationId,
                        displayName: node.text,
                        conversationIdentityType: 'chat',
                    },
                }
                : { user: { id: node.userId, displayName: node.text } },
        });
        return id;
    };
    return { html: teamsNodesToHtml(blocks, mentionId), mentions };
}
//# sourceMappingURL=teams-compose.js.map